import React, { useRef, useState, useEffect, useCallback, useImperativeHandle, useMemo, forwardRef } from 'react';
import { debounce } from 'lodash';
import { PreviewModal } from './PreviewModal';
import { MessageModel } from '../types/models';
import { useAudioRecording } from './message/hooks/useAudioRecording';
import { useWaveformVisualization } from './message/hooks/useWaveformVisualization';
import { useDocumentStore } from '../stores/documentStore';
import { useDocumentSelectionStore } from '../stores/documentSelectionStore';
import { useToastStore } from '../stores/ToastStore';
import { useFileUpload } from '../hooks/useFileUpload';
import { useChatHistoryStore } from '../stores/chatHistoryStore';
import { apiClient, processChatCompletionStream } from '../clients/APIClient';
import { Logger } from '../utils/Logger';

const logger = new Logger('ChatInput');

interface ChatInputProps {
  isGenerating: boolean;
  selectedModel: string;
  selectedFiles: string[];
  attachedImages: string[];
  setSelectedFiles: (files: string[]) => void;
  setAttachedImages: React.Dispatch<React.SetStateAction<string[]>>;
  handleStop: () => void;
  fetchDocuments: () => void;
  isUploadingDocument: boolean;
  setIsUploadingDocument: (uploading: boolean) => void;
  
  // chat function props
  inputs: MessageModel[];
  setInputs: React.Dispatch<React.SetStateAction<MessageModel[]>>;
  setHistory: (history: any) => void;
  chatId: string;
  selectedFilesDoc: any;
  setIsGenerating: (isGenerating: boolean) => void;

  // VLM support prop
  isVLMSupported: boolean;

  // Routes .mag archive files to the parent's magazine import flow.
  onMagazineFiles?: (files: File[]) => void | Promise<void>;
}

export const ChatInput = forwardRef<any, ChatInputProps>(({
  isGenerating,
  selectedModel,
  selectedFiles: _selectedFiles,
  attachedImages,
  setSelectedFiles,
  setAttachedImages,
  handleStop,
  fetchDocuments: _fetchDocuments,
  isUploadingDocument,
  setIsUploadingDocument,
  inputs,
  setInputs,
  setHistory,
  chatId: _chatId,
  selectedFilesDoc,
  setIsGenerating,
  isVLMSupported,
  onMagazineFiles,
}, ref) => {
  const [inputText, setInputText] = useState('');
  const { isFileUploadAborted, isFileUploadAbortedRef, setIsFileUploadAborted, updateDocumentContext } = useDocumentStore();
  const { uploadBase64File, cancelUpload, isUploading } = useFileUpload();
  const inUseVLM = useChatHistoryStore((s) => s.inUseVLM);
  const setInUseVLM = useChatHistoryStore((s) => s.setInUseVLM);
  const setHasImages = useChatHistoryStore((s) => s.setHasImages);
  const hasImages = useChatHistoryStore((s) => s.hasImages);

  // Refs

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const isGeneratingRef = useRef(isGenerating);

  const textareaWrapperRef = useRef<HTMLDivElement>(null);
  const plusButtonRef = useRef<HTMLButtonElement>(null);
  const stopButtonRef = useRef<HTMLButtonElement>(null);

  const { showToast } = useToastStore();
  const waveform = useWaveformVisualization(canvasRef);

  const handleRecordingStart = useCallback((stream: MediaStream) => {
    if (canvasRef.current && textareaRef.current) {
      const wrapper = textareaRef.current.parentElement as HTMLElement;
      if (wrapper) {
        canvasRef.current.width = wrapper.clientWidth - 140;
        canvasRef.current.height = wrapper.clientHeight;
        canvasRef.current.style.left = '70px';
      }
    }
    waveform.start(stream);
  }, [waveform]);

  const handleRecordingStop = useCallback(() => {
    waveform.stop();
  }, [waveform]);

  const canRecordAudio = useCallback(
    () => !isGenerating && inputText.trim().length === 0,
    [isGenerating, inputText],
  );

  const setTranscribedInputText = useCallback<React.Dispatch<React.SetStateAction<string>>>(
    (action) => {
      const text = typeof action === 'function' ? action('') : action;
      setInputText(text);
      if (textareaRef.current) {
        textareaRef.current.value = text;
        const evt = new Event('input', { bubbles: true, cancelable: true });
        textareaRef.current.dispatchEvent(evt);
        requestAnimationFrame(() => {
          textareaRef.current!.scrollTop = textareaRef.current!.scrollHeight;
        });
      }
    },
    [],
  );

  const { isRecording, isTranscribing, handleAudioClick } = useAudioRecording({
    setTranscribedText: setTranscribedInputText,
    canRecord: canRecordAudio,
    onRecordingStart: handleRecordingStart,
    onRecordingStop: handleRecordingStop,
  });

  // Keep parent upload flag in sync with hook state without changing parent API
  useEffect(() => {
    setIsUploadingDocument(isUploading);
  }, [isUploading, setIsUploadingDocument]);

  // When a global cancel is triggered from the toast, propagate to the upload hook
  useEffect(() => {
    if (isFileUploadAborted) {
      void cancelUpload();
    }
  }, [isFileUploadAborted, cancelUpload]);

  // Ensure a full-batch cancel once triggered, even if context flag resets later
  const batchCancelRef = useRef(false);
  useEffect(() => {
    if (isFileUploadAborted) {
      batchCancelRef.current = true;
    }
  }, [isFileUploadAborted]);

  useEffect(() => {
    if (!isGenerating && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [isGenerating]);

  useEffect(() => {
    isGeneratingRef.current = isGenerating;
  }, [isGenerating]);

  const updateStyle = useCallback((_isGenerating: boolean) => {
    const textarea = textareaRef.current;
    const wrapper = textareaWrapperRef.current;
    const plusButton = plusButtonRef.current;
    const stopButton = stopButtonRef.current;
  
    if (!textarea || !wrapper || !plusButton || !stopButton) return;
  
    const BASE_MIN = 65;
    const MAX_H = 185;
  
    textarea.style.height = '0px';
    void textarea.offsetHeight;
  
    const natural = textarea.scrollHeight;
    const target = Math.max(BASE_MIN, Math.min(natural, MAX_H));
  
    requestAnimationFrame(() => {
      textarea.style.overflowY = natural > MAX_H ? 'auto' : 'hidden';
      textarea.style.height = `${target}px`;
      wrapper.style.height = `${target}px`;
  
      const isMultiline = target > BASE_MIN + 1;
      const buttonSize = isMultiline ? '32px' : '44px';
      const iconSize = isMultiline ? '15px' : '22px';
  
      plusButton.style.width = buttonSize;
      plusButton.style.height = buttonSize;
      stopButton.style.width = buttonSize;
      stopButton.style.height = buttonSize;
      
      document.querySelectorAll('.chat-icon').forEach(icon => {
        (icon as HTMLElement).style.fontSize = iconSize;
      });
      
      wrapper.style.borderRadius = isMultiline ? '24px' : '100px';
    });

  }, []);
  
  // Debounced input handler to avoid excessive typing events
  const debouncedHandleInput = useMemo(
    () =>
      debounce((textarea: HTMLTextAreaElement) => {
        setInputText(textarea.value);
      }, 10),
    []
  );
  // Clean up debounced handler on unmount
  useEffect(() => {
    return () => {
      debouncedHandleInput.cancel();
    };
  }, [debouncedHandleInput]);

  useEffect(() => {
    const handleResize = debounce(() => {
      updateStyle(isGeneratingRef.current);
      if (isRecording && textareaWrapperRef.current) {
        const wrapper = textareaWrapperRef.current;
        waveform.resizeCanvas(wrapper.clientWidth - 140, wrapper.clientHeight);
      }
    }, 10);
  
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      handleResize.cancel();
    };
  }, [updateStyle, isRecording, waveform]);
  
  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      const form = event.currentTarget.closest('form');
      if (form) {
        form.requestSubmit();
      }
    }
  };

  const handleInput = (event: React.FormEvent<HTMLTextAreaElement>) => {
      const textarea = event.currentTarget;
      
      debouncedHandleInput(textarea);
      setInputText(textarea.value);
      updateStyle(isGenerating);

      requestAnimationFrame(() => {
        if (textarea.selectionStart === textarea.value.length) {
          textarea.scrollTop = textarea.scrollHeight;
        }
      });

  };
  
  // Helper function to remove the data URI prefix from a base64 string in handleFileChange
  const removeDataUriPrefix = useCallback((base64String: string) => {
    const prefixRegex = /^data:.*;base64,/;
    return base64String.replace(prefixRegex, '');
  }, []);

  // Helper function to trim file names
  function trimFileName(fileName: string, maxLength: number = 25): string {
    
    if (fileName.length <= maxLength) {
      return fileName;
    }

    // Extract extension
    const lastDotIndex = fileName.lastIndexOf('.');
    const ext = lastDotIndex !== -1 ? fileName.substring(lastDotIndex) : '';
    const nameWithoutExtension = lastDotIndex !== -1 ? fileName.substring(0, lastDotIndex) : fileName;

    // Calculate how much of the name to concanate
    const availableLength = maxLength - ext.length - 3; // 3 for "..." length

    if (availableLength <= 0) {
      // Extension is too long, just truncate the whole thing
      return `${fileName.substring(0, maxLength - 3)}...`;
    }

    // Keep start and end parts of the name with ellipsis in the middle
    const leftPartIndex = Math.ceil(availableLength / 2);
    const rightPartIndex = Math.floor(availableLength / 2);

    const leftPart = nameWithoutExtension.substring(0, leftPartIndex);
    const rightPart = nameWithoutExtension.substring(nameWithoutExtension.length - rightPartIndex);
    
    return `${leftPart}...${rightPart}${ext}`;
  }

  const processFiles = useCallback((files: FileList | File[]) => {
    const SUPPORTED_EXTENSIONS = [
      '.docx', '.doc', '.txt', '.log', '.ini', '.rtf', '.odt',
      '.xls', '.xlsx', '.csv', '.pptx', '.pdf', '.epub',
      '.htm', '.html', '.md', '.json', '.xml'
    ];
    const SUPPORTED_IMAGE_EXTENSIONS = ['.png', '.jpg', '.jpeg', '.webp', '.bmp'];
    const SUPPORTED_ARCHIVE_EXTENSIONS = ['.mag'];
    const canProcessImages = isVLMSupported;

    const readAsDataURL = (file: File) => new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

    const handleImages = async (imageFiles: File[]) => {
      if (!canProcessImages || imageFiles.length === 0) return;
      for (const file of imageFiles) {
        try {
          const dataUrl = await readAsDataURL(file);
          const base64Data = removeDataUriPrefix(dataUrl);
          setAttachedImages(prev => [...prev, base64Data]);
        } catch (error) {
          logger.error('Error reading image file:', error);
          showToast('Failed to attach image', 'error', {timeout: 3000, placement: 'chat'});
        }
      }
      showToast(`${imageFiles.length} Image(s) attached`, 'success', {timeout: 3000, placement: 'chat'});
    };

    const handleDocuments = async (docFiles: File[]) => {
      let uploadCount = docFiles.length;
      let successCount = 0;
      let errorCount = 0;
      let currentFileIndex = 0;
      
      
      setIsFileUploadAborted(false);

      for (const file of docFiles) {
        currentFileIndex++;
        if (isFileUploadAbortedRef.current || batchCancelRef.current) {
          logger.log('File upload queue aborted, stopping further uploads');
          break;
        }
        const fileName = trimFileName(file.name);
        // Electron exposes file.path with the full local filesystem path
        const localPath = (file as File & { path?: string }).path;
        
        try {
          showToast('Preparing File Upload', 'upload', {placement: 'chat'});
          const dataUrl = await readAsDataURL(file);
          const base64 = removeDataUriPrefix(dataUrl);

          await uploadBase64File(base64, file.name, {
            purpose: 'assistants',
            onProgress: (progress) => {
              if (batchCancelRef.current || isFileUploadAbortedRef.current) return;
              showToast(`(${currentFileIndex}/${docFiles.length}) Uploading ${fileName} : ${progress.percentage}%`, 'upload', {placement: 'chat', progress: progress.percentage});
            },
            onSuccess: (fileId) => {
              uploadCount -= 1;
              successCount += 1;
              // Source viewing must use the server's masked copy.
              updateDocumentContext({
                operation: 'refresh',
                shouldRefreshServer: true
              });
            },
            onError: (error) => {
              logger.error('Document upload failed.');
              errorCount += 1;
              uploadCount -= 1;
            }
          });
        } catch (e) {
          logger.error('Error preparing/uploading document:', e);
        }
      }

      // Refresh documents and show summary toast if any files were processed
      if (docFiles.length > 0) {
        await updateDocumentContext({
          operation: 'refresh',
          shouldRefreshServer: true
        });
        
        if (batchCancelRef.current || isFileUploadAbortedRef.current) {
          // Reset for next operation
          batchCancelRef.current = false;
          setIsFileUploadAborted(false);
          showToast('Upload cancelled', 'warning', {timeout: 3000, placement: 'chat'});
        } else if (uploadCount === 0 && successCount > 0) {
          showToast(`${successCount} documents uploaded to library`, 'success', {timeout: 5000, placement: 'chat'});
        } else if (errorCount > 0) {
          showToast(`${docFiles.length - successCount} document(s) failed to upload`, 'error', {timeout: 5000, placement: 'chat'});
        }
        
        setIsUploadingDocument(false);
      }
    };

    const imageFiles: File[] = [];
    const docFiles: File[] = [];
    const magFiles: File[] = [];

    Array.from(files).forEach(file => {
      logger.log('Processing file:', file);
      const ext = `.${file.name.split('.').pop()?.toLowerCase() || ''}`;
      if (SUPPORTED_ARCHIVE_EXTENSIONS.includes(ext)) {
        magFiles.push(file);
      } else if (SUPPORTED_IMAGE_EXTENSIONS.includes(ext)) {
        if (canProcessImages) {
          imageFiles.push(file);
        } else {
          showToast(`Images Upload Not Supported With Selected Model`, 'warning', { timeout: 3000, placement: 'chat' });
        }
      } else if (SUPPORTED_EXTENSIONS.includes(ext)) {
        docFiles.push(file);
      } else {
        logger.error('Unsupported file type:', trimFileName(file.name));
        showToast(`Unsupported file type: ${trimFileName(file.name)}`, 'error', {timeout: 3000, placement: 'chat'});
      }
    });

    if (magFiles.length > 0) {
      if (onMagazineFiles) {
        void onMagazineFiles(magFiles);
      } else {
        magFiles.forEach((file) => {
          showToast(`Magazine import not available here: ${trimFileName(file.name)}`, 'error', { timeout: 3000, placement: 'chat' });
        });
      }
    }

    // Fire and forget; sequencing handled inside helpers
    void handleImages(imageFiles);
    void handleDocuments(docFiles);
  }, [
    uploadBase64File,
    isFileUploadAbortedRef,
    batchCancelRef,
    setIsFileUploadAborted,
    showToast,
    updateDocumentContext,
    setIsUploadingDocument,
    setAttachedImages,
    removeDataUriPrefix,
    isVLMSupported,
    onMagazineFiles
  ]);

  const handleChatInput = useCallback(async (input: string) => {
    const currentSelectedDocuments = useDocumentSelectionStore.getState().selectedFilesDoc;
    const documentsForRequest = currentSelectedDocuments.length > 0
      ? currentSelectedDocuments
      : undefined;

    // Create valid image URLs for display
    const imageUrls = attachedImages.length > 0 
      ? attachedImages.map(img => `data:image/jpeg;base64,${img}`) 
      : undefined;
    
    const inputData: MessageModel = { 
      role: 'user',
      content: input,
      documents: documentsForRequest,
      images: imageUrls
    };

    // Add user input to the message history so it can be displayed
    setHistory((prevMessages: MessageModel[]) => [...prevMessages, inputData]);

    // Prepare message data for backend
    const backendInputData: MessageModel = { 
      role: 'user',
      content: input,
      documents: documentsForRequest,
      images: attachedImages && attachedImages.length > 0 ? attachedImages : undefined
    };

    // Add to inputs for backend processing
    setInputs((prevMessages: MessageModel[]) => [...prevMessages, backendInputData]);

    // Set generating state
    setIsGenerating(true);
    updateStyle(isGenerating);

    logger.log('Starting chat completion stream');
    logger.log('RAG Context - selectedFilesDoc:', currentSelectedDocuments);

    // Process the chat using API client SSE streaming
    try {
      const response = await apiClient.chat.chatCompletionStream(
        [...inputs, backendInputData], 
        selectedModel, 
        currentSelectedDocuments,
        true
      );
      
      logger.log('Stream response received:', response);

      // Process the SSE stream - events will be dispatched automatically
      await processChatCompletionStream(response);
    } catch (err) {
      logger.error("Error during chat:", err);
      showToast("An error occurred.", 'error', {timeout: 5000, placement: 'chat'});
	} finally {
		setIsGenerating(false);
    // Notify Chat.tsx to refresh, which triggers incremental search updates in stores
		// Persist is handled by handleChatHistoryUpdate in Chat.tsx via useEffect([isGenerating])
		window.dispatchEvent(new CustomEvent('chat-completion-finished'));
	}
    setSelectedFiles([]);
  }, [attachedImages, selectedFilesDoc, setHistory, setInputs, setIsGenerating, updateStyle, isGenerating, inputs, selectedModel, showToast, setSelectedFiles]);
  
  // Expose methods to parent via ref
  useImperativeHandle(ref, () => ({
    setValue: (text: string) => {
        setInputText(text);
          
        if (textareaRef.current) {
            textareaRef.current.value = text;
            textareaRef.current.focus();
        }
    },
    
    submitMessage: () => {
        // Instead of simulating a form event, directly use the handleChatInput function
        if (textareaRef.current && textareaRef.current.value.trim()) {
            const inputValue = textareaRef.current.value;
            
            // Process the message
            handleChatInput(inputValue);
            
            // Reset the input field
            setInputText('');
            textareaRef.current.value = '';
            setAttachedImages([]);
            
            // Trigger UI update
            const inputEvent = new Event('input', {
                bubbles: true,
                cancelable: true
            });
            textareaRef.current.dispatchEvent(inputEvent);
        }
    },

    processDroppedFiles: (files: FileList | File[]) => {
        processFiles(files);
    },
    textareaRef
}), [processFiles, setInputText, setAttachedImages, handleChatInput]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files) return;
    
    processFiles(files);
    event.target.value = '';
  };

  const chat = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isGenerating) return;
    const formData = new FormData(event.currentTarget);
    const input = formData.get('chat-input') as string;
    
    if (!input) {
      return;
    }
    
    event.currentTarget.reset();
    setInputText('');
    
    handleChatInput(input);

    setAttachedImages([]);
    
    if (textareaRef.current) {
      textareaRef.current.value = '';
      const domEvent = new Event('input', {
        bubbles: true,
        cancelable: true,
      });
      textareaRef.current.dispatchEvent(domEvent);
    }
  };

  const handleRemoveImage = (imageToRemove: string, index?: number) => {
    // If index is provided, remove by index (position)
    if (index !== undefined) {
      setAttachedImages(prev => prev.filter((_, i) => i !== index));
      return;
    }
    
    // Fallback to the current content-based removal
    const normalizedImageToRemove = imageToRemove.includes('base64,')
      ? imageToRemove.split('base64,')[1]
      : imageToRemove;

    setAttachedImages(prev => prev.filter(image => image !== normalizedImageToRemove));
  };
  
  // Handle image copy/paste from clipboard
  const handleImageCopy = (event: React.ClipboardEvent<HTMLTextAreaElement>) => {
    if (isGenerating) return;
    const images = event.clipboardData?.items;
    if (!images || images.length === 0) return;
    // Restrict to only copy/paste supported image types
    const SUPPORTED_IMAGE_MIME_TYPES = {
      'image/png': '.png',
      'image/jpeg': '.jpg',
      'image/jpg': '.jpg',
      'image/webp': '.webp',
      'image/bmp': '.bmp'
    };

    let imageCt = 0;
    let supportedCt = 0;
    let unsupportedCt = 0;
    const unsupportedTypes: string[] = [];
    let processingCt = 0;
    
    // Look for all image content in clipboard
    for (let i = 0; i < images.length; i++) {
      const item = images[i];
      const itemType = item.type;

      // Check if it's an image
      if (itemType.indexOf('image') !== -1) {
        imageCt++;
        const isSupported = Object.keys(SUPPORTED_IMAGE_MIME_TYPES).includes(itemType);
        
        if (!isSupported) {
          const ext = itemType.split('/')[1];
          unsupportedCt++;
          unsupportedTypes.push(ext);
          continue;
        }

        supportedCt++;

        // Prevent default paste only if we found at least one image
        event.preventDefault();
        
        // Get the image from clipboard
        const blob = item.getAsFile();
        if (!blob) continue;

        processingCt++;
        setIsUploadingDocument(true);

        if (imageCt === 1) {
          showToast('Processing pasted image', 'upload', {placement: 'chat'});
        }

        // Convert blob to base64
        const reader = new FileReader();
        reader.onload = () => {
          if (typeof reader.result === 'string') {
            const base64Data = removeDataUriPrefix(reader.result);
            
            // Add to attachedImages
            setAttachedImages(prev => [...prev, base64Data]);
            
            // Decrement processing counter
            processingCt--;

            // If all images are processed, update UI
            if (processingCt === 0) {
              setIsUploadingDocument(false);
              
              // Show final toast with results
              if (unsupportedTypes.length > 0) {
                const formatList = unsupportedTypes.join(', ').toUpperCase();
                showToast(`${unsupportedCt} image(s) failed to upload. Unsupported format(s): [ ${formatList} ]`, 'warning', {timeout: 7000, placement: 'chat'});
              } else {
                showToast(`${supportedCt} image(s) uploaded successfully`, 'success', {timeout: 5000, placement: 'chat'});
              }
            }
          }
        };
        // Reader error handling
        reader.onerror = () => {
          processingCt--;
          if (processingCt === 0) {
            setIsUploadingDocument(false);
            showToast('Failed to process pasted image', 'error', {timeout: 7000, placement: 'chat'});
          }
        };
        reader.readAsDataURL(blob);
      }
    }
    
    if (imageCt > 0 && supportedCt === 0 && unsupportedTypes.length > 0) {
      const formatList = unsupportedTypes.join(', ').toUpperCase();
      showToast(`Unsupported image format(s): [ ${formatList} ]. Please use PNG, JPEG, WEBP, or BMP`, 'warning', {timeout: 7000, placement: 'chat'});
      event.preventDefault();
    }
  };
  useEffect(() => {
    
    const hasAttachedImages = attachedImages.length > 0;
    const hasImagesInHistory = inputs.some(message => 
      message.images && message.images.length > 0
    );
    const needsVLM = hasAttachedImages || hasImagesInHistory;
    
    if (needsVLM !== hasImages || needsVLM !== inUseVLM) {
      setHasImages(needsVLM);
      setInUseVLM(needsVLM);
    }
  }, [attachedImages, inputs, hasImages, inUseVLM, setHasImages, setInUseVLM]);

  
  return (
    <div className="w-full max-w-[90%] mx-auto pt-md relative">
      
      <input
        type="file"
        id="file-input"
        accept={isVLMSupported
          ? ".docx, .doc, .txt, .jpeg, .jpg, .png, .webp, .bmp, .log, .ini, .rtf, .odt, .xls, .xlsx, .csv, .pptx, .pdf, .epub, .htm, .html, .md, .json, .xml, .mag"
          : ".docx, .doc, .txt, .log, .ini, .rtf, .odt, .xls, .xlsx, .csv, .pptx, .pdf, .epub, .htm, .html, .md, .json, .xml, .mag"}
        multiple
        style={{ display: 'none' }}
        onChange={handleFileChange}
        disabled={isUploadingDocument}
      />
      
      {attachedImages.length > 0 && (
        <div className="mb-4">
          <PreviewModal 
            files={attachedImages.map(img => `data:image/jpeg;base64,${img}`)} 
            onRemoveFile={(imageUrl, index) => handleRemoveImage(imageUrl, index)}
          />
        </div>
      )}

      <p className="text-xs text-neutral-200 text-center mb-sm">Document uploads save masked text only; originals are discarded.</p>
      <form className='mb-lg max-w-[90%] mx-auto' onSubmit={chat}>
        <div ref={textareaWrapperRef} className={`chat-input-wrapper relative flex h-[65px] overflow-hidden bg-neutral-500 border border-neutral-500 rounded-pill max-h-[300px]${isGenerating ? ' chat-input-wrapper--busy' : ''}`}>
          <button 
            type="button" 
            id='plusbutton'
            ref={plusButtonRef}
            className="chat-button w-11 h-11 bottom-[8.5px] left-2.5"
            title='Upload file to library'
            aria-label='Upload file'
            onClick={() => document.getElementById('file-input')?.click()}
            disabled={isUploadingDocument || (inUseVLM && !isVLMSupported)}
          >
            <i className={`fas fa-paperclip chat-icon ${isUploadingDocument ? 'chat-icon-disabled' : ''}`} />
          </button>

          <textarea
            name="chat-input"
            id="chat-input"
            ref={textareaRef}
            data-testid="chat-input"
            className="chat-textarea flex-1 pt-[20px] pb-[20px] pl-[70px] pr-[60px] max-h-[100%] h-auto"
            onChange={() => {}}
            placeholder={isRecording ? "" : "Enter your message"}
            disabled={!selectedModel || (inUseVLM && !isVLMSupported)}
            onKeyDown={handleKeyDown}
            onInput={handleInput}
            onPaste={handleImageCopy}
    
          />

          <canvas
            ref={canvasRef}
            id="waveform-canvas"
            className="waveform-canvas"
            style={{
              display: isRecording ? 'block' : 'none',
            }}
           />
          <button 
            type="button" 
            id="startstopbutton"
            ref={stopButtonRef}
            className="chat-button w-11 h-11 bottom-[8.5px] right-[12.5px]"
            title={
              isGenerating ? "Stop generation" : 
              inputText.trim().length > 0 ? "Send message" : 
              isTranscribing ? "Transcribing..." : 
              isRecording ? "Stop recording" : "Record voice message"
            }
            onClick={(e) => {
              if (isGenerating) {
                handleStop();
              } else if (inputText.trim().length > 0) {
                const form = e.currentTarget.closest('form');
                if (form) form.requestSubmit();
              } else {
                handleAudioClick();
              }
            }}
            disabled={!selectedModel || isTranscribing || (inUseVLM && !isVLMSupported)}
          >
            {isGenerating ? (
              <i className="fas fa-stop chat-icon" />
            ) : inputText.trim().length > 0 ? (
              <i className="fas fa-paper-plane chat-icon" />
            ) : isTranscribing ? (
              <i className="fas fa-spinner chat-icon-spinner" />
            ) : (
              <i className="fas fa-microphone chat-icon" style={{ color: isRecording ? 'red' : 'white' }} />
            )}
          </button>
        </div>
      </form>
    </div>
  );
});


